import {
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type PointerEvent
} from "react";

import { cn } from "@/lib/utils";

const KEY_STEP = 16;

type Props = {
  width: number;
  /** The live width while dragging; `null` when the drag ends. */
  onDrag: (width: number | null) => void;
  onCommit: (width: number) => void;
  /** Keeps a width inside the panel's bounds. */
  clamp: (width: number) => number;
  /** Lands a released drag on a nearby stop; identity when absent. */
  snap?: (width: number) => number;
  /** Where double-click resets to. */
  defaultWidth: number;
  label: string;
  testId?: string;
  className?: string;
};

/**
 * The left edge of a right-anchored panel: drag to resize (pointer capture
 * keeps the drag when the pointer leaves the strip), double-click to reset,
 * arrow keys nudge.
 */
export const ResizeEdge: FC<Props> = ({
  width,
  onDrag,
  onCommit,
  clamp,
  snap = (w) => w,
  defaultWidth,
  label,
  testId,
  className
}) => {
  const drag = useRef<{ x: number; width: number; last: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, width, last: width };
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    d.last = clamp(d.width + (d.x - e.clientX));
    onDrag(d.last);
  };
  const end = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    onDrag(null);
    onCommit(clamp(snap(d.last)));
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const step = e.key === "ArrowLeft" ? KEY_STEP : -KEY_STEP;
    onCommit(clamp(width + step));
  };

  return (
    <>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={width}
        // A focusable separator is the ARIA pattern for a keyboard-resizable splitter.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        title="Drag to resize. Double-click to reset."
        data-testid={testId}
        data-dragging={dragging || undefined}
        className={cn(
          "absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize touch-none outline-none",
          "before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:transition-colors before:duration-100 hover:before:bg-primary focus-visible:before:bg-primary data-dragging:before:bg-primary",
          className
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        onDoubleClick={() => onCommit(clamp(defaultWidth))}
        onKeyDown={onKeyDown}
      />
      {dragging ? (
        <div className="pointer-events-none absolute top-13 right-3 z-20 rounded-md bg-elevated px-1.5 py-0.5 font-mono text-sm text-fg tabular-nums ring-1 ring-line-2">
          {`${width}px`}
        </div>
      ) : null}
    </>
  );
};
